import React from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { ArrowRight, Phone } from 'lucide-react';
import { SiWhatsapp } from 'react-icons/si';

const Hero: React.FC = () => {
  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Parallax effect for Mini
  const [mouse, setMouse] = React.useState({ x: 0, y: 0 });
  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setMouse({
      x: e.clientX - rect.left - rect.width / 2,
      y: e.clientY - rect.top - rect.height / 2,
    });
  };

  // Framer Motion values for parallax
  const x = useSpring(mouse.x / 20, { stiffness: 100, damping: 20 });
  const y = useSpring(mouse.y / 20, { stiffness: 100, damping: 20 });

  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/background/desktop-wallpaper-mini-cooper-classic.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
      onMouseMove={handleMouseMove}
    >
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-10">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-mini-red rounded-full mix-blend-multiply filter blur-xl"
          animate={{
            x: [0, 100, 0],
            y: [0, -100, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-mini-green rounded-full mix-blend-multiply filter blur-xl"
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
      </div>

      {/* Mini Silhouette with Parallax */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center opacity-5"
        style={{ x, y }}
      >
        <svg 
          width="384" 
          height="192" 
          viewBox="0 0 120 60" 
          className="w-96 h-48"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Car body */}
          <defs>
            <linearGradient id="carGradientHero" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="100%" stopColor="#F3F4F6" />
            </linearGradient>
          </defs>
          {/* Main body */}
          <ellipse cx="60" cy="45" rx="45" ry="12" fill="url(#carGradientHero)" stroke="#FFFFFF" strokeWidth="1" opacity="0.8"/>
          <ellipse cx="60" cy="35" rx="25" ry="8" fill="#FFFFFF" opacity="0.6"/>
          <ellipse cx="25" cy="42" rx="8" ry="6" fill="#FFFFFF" opacity="0.7"/>
          <ellipse cx="95" cy="42" rx="8" ry="6" fill="#FFFFFF" opacity="0.7"/>
          <circle cx="18" cy="40" r="3" fill="#FFFFFF" opacity="0.9"/>
          <circle cx="102" cy="40" r="2.5" fill="#FFFFFF" opacity="0.8"/>
          <circle cx="35" cy="52" r="8" fill="#FFFFFF" opacity="0.6"/>
          <circle cx="85" cy="52" r="8" fill="#FFFFFF" opacity="0.6"/>
        </svg>
      </motion.div>

      {/* Driving Mini Animation */}
      <div 
        className="mini-drive pointer-events-none"
        style={{
          animation: 'driveMini 15s linear infinite',
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 10
        }}
      >
        <svg 
          width="120" 
          height="60" 
          viewBox="0 0 120 60" 
          className="drop-shadow-lg"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Car body */}
          <defs>
            <linearGradient id="carGradientDriving" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#DC2626" />
              <stop offset="100%" stopColor="#B91C1C" />
            </linearGradient>
          </defs>
          
          {/* Main body */}
          <ellipse cx="60" cy="45" rx="45" ry="12" fill="url(#carGradientDriving)" stroke="#B91C1C" strokeWidth="1"/>
          <ellipse cx="60" cy="35" rx="25" ry="8" fill="#FFFFFF" stroke="#D1D5DB" strokeWidth="1"/>
          <ellipse cx="25" cy="42" rx="8" ry="6" fill="#DC2626"/>
          <ellipse cx="95" cy="42" rx="8" ry="6" fill="#DC2626"/>
          <circle cx="18" cy="40" r="3" fill="#FFF" stroke="#D1D5DB" strokeWidth="1"/>
          <circle cx="18" cy="40" r="1.5" fill="#FBBF24"/>
          <circle cx="102" cy="40" r="2.5" fill="#EF4444"/>
          <circle cx="35" cy="52" r="8" fill="#374151" stroke="#111827" strokeWidth="2"/>
          <circle cx="35" cy="52" r="4" fill="#6B7280"/>
          <circle cx="85" cy="52" r="8" fill="#374151" stroke="#111827" strokeWidth="2"/>
          <circle cx="85" cy="52" r="4" fill="#6B7280"/>
          <ellipse cx="50" cy="35" rx="8" ry="5" fill="#93C5FD" opacity="0.7"/>
          <ellipse cx="70" cy="35" rx="8" ry="5" fill="#93C5FD" opacity="0.7"/>
          <line x1="60" y1="32" x2="60" y2="38" stroke="#D1D5DB" strokeWidth="1"/>
          <rect x="20" y="40" width="80" height="2" fill="#FFFFFF" opacity="0.8"/>
        </svg>
      </div>

      <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1 
            className="text-4xl sm:text-7xl lg:text-8xl font-extrabold font-heading text-white mb-6 tracking-tight drop-shadow-lg"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.span
              initial={{ letterSpacing: '-0.05em' }}
              animate={{ letterSpacing: '0.01em' }}
              transition={{ duration: 1.2, delay: 0.3 }}
              className="block"
            >
              Keeping Classic
            </motion.span>
            <motion.span
              className="text-mini-red block"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              Minis Alive
            </motion.span>
          </motion.h1>
          
          <motion.p 
            className="text-xl sm:text-2xl text-white mb-8 max-w-3xl mx-auto leading-relaxed drop-shadow"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Expert autobody repairs, restorations & authentic parts for Minis from the{' '}
            <span className="text-white font-semibold">60s–90s</span>
          </motion.p>

          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <motion.button
              onClick={() => scrollToSection('#services')}
              className="flex items-center space-x-2 px-6 py-3 rounded-full bg-white/90 text-black font-semibold border-2 border-mini-red shadow hover:bg-mini-red hover:text-white transition-all duration-200"
              whileHover={{ scale: 1.08, rotate: -2 }}
              whileTap={{ scale: 0.97, rotate: 1 }}
            >
              <ArrowRight size={22} className="text-mini-red group-hover:text-white transition-colors" />
              <span className="tracking-wide">Explore Services</span>
            </motion.button>
            <motion.a
              href="tel:+27729981365"
              className="flex items-center space-x-2 px-6 py-3 rounded-full bg-white/90 text-black font-semibold border-2 border-mini-red shadow hover:bg-mini-red hover:text-white transition-all duration-200"
              whileHover={{ scale: 1.08, rotate: 2 }}
              whileTap={{ scale: 0.97, rotate: -1 }}
            >
              <Phone size={22} className="text-mini-red group-hover:text-white transition-colors" />
              <span className="tracking-wide">Call Us</span>
            </motion.a>
            <motion.a
              href="https://wa.me/27729981365"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-6 py-3 rounded-full bg-white/90 text-black font-semibold border-2 border-mini-red shadow hover:bg-mini-red hover:text-white transition-all duration-200"
              whileHover={{ scale: 1.08, rotate: 2 }}
              whileTap={{ scale: 0.97, rotate: -1 }}
            >
              {(SiWhatsapp as any)({ size: 22, className: "text-mini-red group-hover:text-white transition-colors" })}
              <span className="tracking-wide">WhatsApp Us!</span>
            </motion.a>
          </motion.div>

          {/* Stats */}
          <motion.div 
            className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            {[
              { number: '30+', label: 'Years Experience' },
              { number: '250+', label: 'Minis Restored' },
              { number: 'M-F', label: '8AM - 5PM' },
              { number: '100%', label: 'Satisfaction' },
            ].map((stat, index) => (
              <motion.div 
                key={index}
                className="flex flex-col items-center justify-center"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
              >
                <div className="rounded-full bg-black/70 backdrop-blur-md px-6 py-4 mb-2 shadow-lg border-2 border-white flex flex-col items-center min-w-[90px]">
                  <div className="text-2xl sm:text-3xl font-bold text-white mb-1 drop-shadow">
                    {stat.number}
                  </div>
                  <div className="text-sm sm:text-base text-white drop-shadow">
                    {stat.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
        >
          <motion.div 
            className="w-6 h-10 border-2 border-white rounded-full flex justify-center"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.div 
              className="w-1 h-3 bg-white rounded-full mt-2"
              animate={{ y: [0, 10, 0], opacity: [1, 0, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
